<template>
  <div>
    <v-container fluid>
      <v-row dense class="pb-1">
        <v-col cols="3">
          <date-range :from.sync="from" :to.sync="to" v-model="dateDisplay" @change="refresh()" option="Last 30 days" hide-details/>
        </v-col>
        <v-col cols="2">
          <v-autocomplete label="Show" :items="['Quantity','Value']" outlined dense hide-details v-model="show"/>
        </v-col>
        <v-col cols="3">
          <inventory-categories-select v-model="categoryId" label="Category" type="Item" none-text="All" @change="refresh()" hide-details/>
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="printHtml('Item period summary', report())"><v-icon>mdi-printer</v-icon></v-btn>
          <v-tooltip bottom open-delay="1000" v-if="company.allowCSVExport">
            <template v-slot:activator="{on}">
              <v-btn icon @click="exportCSV" v-on="on"><v-icon>mdi-download</v-icon></v-btn>
            </template>
            Export as CSV file
          </v-tooltip>
        </v-col>
      </v-row>
      <v-row dense class="pb-0">
        <v-col cols="3" v-if="sites.length>1">
          <v-autocomplete label="Site" dense outlined hide-details :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="siteId" item-text="name" item-value="id" @change="refresh()"/>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 228px)">
      <span v-html="report()"/>
    </v-card>

  </div>
</template>

<script>
import {add,format} from 'date-fns';
import fileSaver from 'file-saver';
import printHtml from '@/mixins/printHtml'
import InventoryCategoriesSelect from '@/views/inventory/categories/InventoryCategoriesSelect'

export default {
  name: 'ReportsItemsPeriodSummary',
  mixins: [printHtml],
  components: {InventoryCategoriesSelect},
  data() {
    return {
      from: null,
      to: null,
      dateDisplay: '',
      categoryId: null,
      siteId: null,
      show: 'Quantity',
      loading: true,
      items: [],
      totals: {start:0,end:0}
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(true);
    }
  },
  async created() {
    this.$emit('name', 'Items period summary')
    if (this.$route.query.from && this.$route.query.to) {
      this.from = this.$route.query.from;
      this.to = this.$route.query.to;
    }
  },
  methods: {
    async refresh() {
      this.loading = true;
      const start = [];
      const end = [];
      const filters = [{field:'active', comparator:'eq', value:'true'},{field:'type', comparator:'eq', value:'Item'}];
      if (this.categoryId)
        filters.push({field:'categoryId', comparator:'in', value:this.categoryId});
      await Promise.all([
        this.$lazyListLoad(start, 'items2',
                          'id,name,trackUnits,precision,sites {siteId,quantity,value}', 100,
                          filters,[{name:'asof', value:format(add(new Date(this.from+'T23:59:59'), {days:-1}),'yyyy-MM-dd HH:mm:ss')}]),
        this.$lazyListLoad(end, 'items2',
                          'id,sites {siteId,quantity,value}', 100,
                          filters,[{name:'asof',value:`${this.to} 23:59:59`}])
      ]);
      start.forEach(item => item.totalsStart = this.buildTotals(item.sites));
      this.items = start;
      end.forEach(endItem => {
        const item = this.items.find(item => item.id === endItem.id);
        item.totalsEnd = this.buildTotals(endItem.sites);
      });

      this.totals = this.items.reduce(({start,end}, item) => (
        {start:start + item.totalsStart.Value, end:end + item.totalsEnd.Value}
      ), {start:0,end:0});
      this.loading = false;
    },

    buildTotals(sites) {
      return sites.reduce(({Quantity,Value},site) => {
        if (!this.siteId || this.siteId === site.siteId)
          return {Quantity: Quantity + site.quantity, Value: Value + site.value};
        return {Quantity,Value};
      }, {Quantity:0,Value:0});
    },

    report() {
      const lines = [
                  `<style>
                    @page {size:portrait}
                    .reports-items-summary {font-family: Roboto, sans-serif; width:100%;border-spacing:0;font-size:12px}
                    .reports-items-summary th {height:36px;padding:0 16px;vertical-align:middle}
                    .reports-items-summary .spacer {height:18px}
                    .reports-items-summary td {height:36px;padding:0 16px;vertical-align:middle}
                    .reports-items-summary .title {font-size:18px;text-transform:uppercase}
                    .reports-items-summary .sup-title {font-weight:normal;font-size:22px}
                    .reports-items-summary .sub-title {font-weight:normal;height:inherit;padding-bottom:20px;font-size:14px}
                    .reports-items-summary .header th {border-bottom:1px solid #000;margin-bottom:20px}
                    .reports-items-summary .text-left {text-align:left}
                    .reports-items-summary .text-right {text-align:right}
                    .reports-items-summary .font-weight-bold {font-weight:bold}
                    .reports-items-summary .total-row {font-weight:bold}
                    .reports-items-summary .capitalize {text-transform: capitalize}
                  </style>
                   <table class="reports-items-summary">
                     <thead>
                        <tr>
                          <th colspan="6" class="sup-title">${this.$store.getters.company.name}</th>
                        </tr>
                        <tr>
                          <th colspan="6" class="title">Items period summary</th>
                        </tr>
                        <tr>
                           <th colspan="6" class="sub-title">${this.dateDisplay}</th>
                        </tr>

                        <tr class="header">
                          <th width="40%" class="text-left">Item</th>
                          <th width="20%" class="text-right">Start ${this.show}</th>
                          <th width="20%" class="text-right">Change ${this.show}</th>
                          <th width="20%" class="text-right">End ${this.show}</th>
                        </tr>
                      </thead>
                      <tbody>`
      ];
      for (const item of this.items) {
        lines.push(`<tr>`);
        lines.push(`<td>${item.name}</td>`);
        lines.push(`<td class="text-right">${this.number(item.totalsStart[this.show],item)}</td>`);
        lines.push(`<td class="text-right">${this.number(item.totalsEnd[this.show]-item.totalsStart[this.show],item)}</td>`);
        lines.push(`<td class="text-right">${this.number(item.totalsEnd[this.show],item)}</td>`);
      }
      if (this.show === 'Value')
      lines.push(`<tr :class="dark ? 'total-row-dark' : 'total-row'">
                    <td class="text-right">Total</td>
                    <td class="text-right">${this.number(this.totals.start)}</td>
                    <td class="text-right">${this.number(this.totals.end-this.totals.start)}</td>
                    <td class="text-right">${this.number(this.totals.end)}</td>
                  </tr>`);
      lines.push(`</tbody>`);
      lines.push(`</table>`);
      return lines.join('');
    },
    number(num, item, showUnits=true) {
      if (this.show === 'Value')
        return `$${this.$number(num, 2)}`;
      return `${this.$number(num, item.precision)} ${showUnits?item.trackUnits:''}`;
    },

    exportCSV() {
      const lines = [ `"${this.company.name}",,,`,
                      `"Items period summary",,,`,
                      `"${this.dateDisplay}",,,`,
                      `"Item","Start ${this.show}","Change ${this.show}","End ${this.show}"${this.show==='Quantity'?`,"UOM"`:''}`
                    ];
      for (const item of this.items)
        lines.push(`"${item.name}","${this.number(item.totalsStart[this.show],item,false)}","${this.number(item.totalsEnd[this.show]-item.totalsStart[this.show],item,false)}","${this.number(item.totalsEnd[this.show],item,false)}","${item.trackUnits}"`);
      if (this.show === 'Value')
        lines.push(`"Total","${this.number(this.totals.start)}","${this.number(this.totals.end-this.totals.start)}","${this.number(this.totals.end)}"`);

      // Write CSV file
      const blob = new Blob([lines.join('\r\n')], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, 'Items period summary.csv');
    }
  }
}
</script>